import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { titleCase } from "../lib/format";
import brandMark from "../assets/faviconjkmaxx.svg";

const NAV = [
  { to: "/", label: "Overview", icon: "◱", end: true },
  { to: "/colours", label: "Colours", icon: "◐" },
  { to: "/audience", label: "Audience", icon: "◔" },
  { to: "/engagement", label: "Engagement", icon: "◈" },
  { to: "/usage", label: "AI spend", icon: "◇" },
];

const NAV_PEOPLE = [
  { to: "/customers", label: "Customers", icon: "☰" },
  { to: "/leads", label: "Leads", icon: "★" },
  { to: "/sessions", label: "Sessions", icon: "⛨" },
  // Next to Sessions on purpose. The two are read together: a caller being
  // refused and a caller signed in from eleven addresses are the same
  // investigation, and blocking is the step after ending a session.
  { to: "/suspicious", label: "Suspicious activity", icon: "⚠" },
  { to: "/renders", label: "Render wall", icon: "▦" },
];

// Its own group, above Console: this is the only section that writes to what
// customers see, and filing it under People (who used the product) or
// Analytics (what they did) would bury it.
const NAV_CONTENT = [
  { to: "/templates", label: "Room templates", icon: "▣" },
];

const NAV_ADMIN = [
  { to: "/audit", label: "Audit log", icon: "≡" },
  { to: "/team", label: "Admin team", icon: "⚑" },
];

// The toggle has three states and only two are stored: "system" is the absence
// of a stamp, which is what lets the OS setting keep working.
type Theme = "system" | "light" | "dark";
const THEME_KEY = "jkmaxx.admin.theme";

function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem(THEME_KEY) as Theme) || "system";
    } catch {
      return "system";
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* private mode — the choice just does not persist */
    }
  }, [theme]);

  return [theme, setTheme];
}

export function AppShell() {
  const { admin, signOut } = useAuth();
  const [theme, setTheme] = useTheme();

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <img className="brand-mark" src={brandMark} alt="" aria-hidden />
          <div>
            <div className="brand-name">JK Maxx</div>
            <div className="brand-sub">Admin</div>
          </div>
        </div>

        <nav className="nav">
          <span className="nav-label">Analytics</span>
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              <span className="nav-icon" aria-hidden>
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}

          <span className="nav-label">People</span>
          {NAV_PEOPLE.map((item) => (
            <NavLink key={item.to} to={item.to}>
              <span className="nav-icon" aria-hidden>
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}

          <span className="nav-label">Content</span>
          {NAV_CONTENT.map((item) => (
            <NavLink key={item.to} to={item.to}>
              <span className="nav-icon" aria-hidden>
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}

          <span className="nav-label">Console</span>
          {NAV_ADMIN.map((item) => (
            <NavLink key={item.to} to={item.to}>
              <span className="nav-icon" aria-hidden>
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="who">
            <strong>{admin?.fullName}</strong>
            <span>{admin?.email}</span>
            <span>{titleCase(admin?.role)}</span>
          </div>

          <div className="segmented" role="group" aria-label="Theme">
            {(["system", "light", "dark"] as const).map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={theme === t}
                onClick={() => setTheme(t)}
                style={{ flex: 1, padding: "5px 6px", fontSize: 12 }}
              >
                {t === "system" ? "Auto" : t === "light" ? "Light" : "Dark"}
              </button>
            ))}
          </div>

          <button className="btn btn-sm" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="main">
        <Outlet />
      </div>
    </div>
  );
}

/** Sticky header + padded body. Every page renders through this. */
export function Page({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <header className="topbar">
        <div>
          <h1 className="page-title">{title}</h1>
          {subtitle && <p className="page-sub">{subtitle}</p>}
        </div>
        <div className="topbar-spacer" />
        {actions}
      </header>
      <div className="content">{children}</div>
    </>
  );
}
